import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../services/server/axios";
import Loader from "../../../components/loader/loader";

const PayopCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get all parameters from URL for debugging
        const allParams = {};
        for (const [key, value] of searchParams.entries()) {
          allParams[key] = value;
        }

        console.log("PayOP callback received all parameters:", allParams);

        // Get specific parameters we expect
        const invoiceId = searchParams.get("invoiceId") || searchParams.get("invoice_id") || searchParams.get("id");
        const orderId = searchParams.get("orderId") || searchParams.get("order_id");
        const statusParam = searchParams.get("status") || searchParams.get("state");
        const offerId = searchParams.get("offer_id") || searchParams.get("offerId");

        console.log("PayOP callback parsed:", { invoiceId, orderId, statusParam, offerId });

        // Check if we have a pending payment in localStorage
        const pendingPaymentStr = localStorage.getItem("pendingPayopPayment");
        let pendingPayment = null;

        if (pendingPaymentStr) {
          pendingPayment = JSON.parse(pendingPaymentStr);
          console.log("Found pending PayOP payment:", pendingPayment);
        }

        // If we have status parameter from PayOP redirect (success/failed)
        if (statusParam === "success" || statusParam === "completed") {
          // Payment successful via redirect
          setStatus("success");
          setMessage("Payment completed successfully!");

          // Clear pending payment
          localStorage.removeItem("pendingPayopPayment");

          // Try to get achat ID from backend using invoiceId or orderId
          const identifier = invoiceId || orderId;
          if (identifier) {
            try {
              const response = await axiosInstance.get(`/payop/status/${identifier}`);

              if (response.data.success && response.data.data?.achat_id) {
                setTimeout(() => {
                  navigate(`/complete-purchase/${response.data.data.achat_id}`);
                }, 1500);
                return;
              }
            } catch (error) {
              console.error("Error fetching payment status:", error);
            }
          }

          // If we have pending payment with offerId, try to confirm payment
          if (pendingPayment?.offerId) {
            try {
              const response = await axiosInstance.post("/payop/confirm-payment", {
                invoice_id: invoiceId || orderId || pendingPayment.invoiceId,
                offer_id: pendingPayment.offerId,
                amount: pendingPayment.amount,
                status: "success"
              });

              if (response.data.success && response.data.data?.achat_id) {
                setTimeout(() => {
                  navigate(`/complete-purchase/${response.data.data.achat_id}`);
                }, 1500);
                return;
              }
            } catch (error) {
              console.error("Error confirming payment:", error);
            }
          }

          // If we have pending payment with achatId or achatOffreId, navigate directly
          if (pendingPayment?.achatId) {
            setTimeout(() => {
              navigate(`/complete-purchase/${pendingPayment.achatId}`);
            }, 1500);
            return;
          } else if (pendingPayment?.achatOffreId) {
            setTimeout(() => {
              navigate(`/complete-purchase/${pendingPayment.achatOffreId}`);
            }, 1500);
            return;
          }

          // Navigate to dashboard as fallback
          setTimeout(() => {
            navigate("/dashboard");
          }, 3000);
          return;

        } else if (statusParam === "failed" || statusParam === "cancelled" || statusParam === "error") {
          // Payment failed via redirect
          setStatus("failed");
          setMessage("Payment was cancelled or failed.");
          localStorage.removeItem("pendingPayopPayment");

          setTimeout(() => {
            navigate("/dashboard");
          }, 3000);
          return;
        }

        // If we have invoiceId or orderId but no status param, check payment status
        if (invoiceId || orderId) {
          const identifier = invoiceId || orderId;

          try {
            const response = await axiosInstance.get(`/payop/payment-status/${identifier}`);

            if (response.data.success) {
              const paymentStatus = response.data.data?.status;
              const achatId = response.data.data?.achat_id;

              if (paymentStatus === "success" || paymentStatus === "completed") {
                // Payment successful
                setStatus("success");
                setMessage("Payment completed successfully!");

                // Clear pending payment
                localStorage.removeItem("pendingPayopPayment");

                // Navigate to complete purchase if we have achatId
                if (achatId) {
                  setTimeout(() => {
                    navigate(`/complete-purchase/${achatId}`);
                  }, 1500);
                } else if (pendingPayment?.achatId) {
                  // Use stored achatId from localStorage
                  setTimeout(() => {
                    navigate(`/complete-purchase/${pendingPayment.achatId}`);
                  }, 1500);
                } else if (pendingPayment?.offerId) {
                  // Try to create achat record
                  try {
                    const confirmResponse = await axiosInstance.post("/payop/confirm-payment", {
                      invoice_id: identifier,
                      offer_id: pendingPayment.offerId,
                      amount: pendingPayment.amount,
                      status: "success"
                    });

                    if (confirmResponse.data.success && confirmResponse.data.data?.achat_id) {
                      setTimeout(() => {
                        navigate(`/complete-purchase/${confirmResponse.data.data.achat_id}`);
                      }, 1500);
                    } else {
                      setTimeout(() => {
                        navigate("/dashboard");
                      }, 3000);
                    }
                  } catch (error) {
                    console.error("Error confirming payment:", error);
                    setTimeout(() => {
                      navigate("/dashboard");
                    }, 3000);
                  }
                } else {
                  setTimeout(() => {
                    navigate("/dashboard");
                  }, 3000);
                }
                return;
              } else if (paymentStatus === "failed" || paymentStatus === "cancelled") {
                setStatus("failed");
                setMessage("Payment was cancelled or failed.");
                localStorage.removeItem("pendingPayopPayment");

                setTimeout(() => {
                  navigate("/dashboard");
                }, 3000);
                return;
              } else if (paymentStatus === "pending" || paymentStatus === "processing") {
                setStatus("processing");
                setMessage("Payment is still processing. Please wait...");

                // Check again after 5 seconds
                setTimeout(() => {
                  handleCallback();
                }, 5000);
                return;
              }
            }
          } catch (error) {
            console.error("Error checking payment status:", error);
          }
        }

        // If we have pending payment but no invoiceId/orderId in URL
        if (pendingPayment) {
          setStatus("checking_pending");
          setMessage("Checking your payment status...");

          // Try to check payment status using stored info
          try {
            let response;

            if (pendingPayment.invoiceId) {
              response = await axiosInstance.get(`/payop/status/${pendingPayment.invoiceId}`);
            }

            if (response?.data?.success) {
              const paymentStatus = response.data.data?.status;

              if (paymentStatus === "success" || paymentStatus === "completed") {
                setStatus("success");
                setMessage("Payment completed successfully!");
                localStorage.removeItem("pendingPayopPayment");

                if (response.data.data?.achat_id) {
                  setTimeout(() => {
                    navigate(`/complete-purchase/${response.data.data.achat_id}`);
                  }, 1500);
                } else if (pendingPayment?.achatId) {
                  setTimeout(() => {
                    navigate(`/complete-purchase/${pendingPayment.achatId}`);
                  }, 1500);
                } else {
                  setTimeout(() => {
                    navigate("/dashboard");
                  }, 3000);
                }
                return;
              }
            }
          } catch (error) {
            console.error("Error checking pending payment:", error);
          }
        }

        // Default fallback - check if payment might be in localStorage from other tab
        setTimeout(() => {
          const checkAgain = localStorage.getItem("pendingPayopPayment");
          if (checkAgain) {
            console.log("Retrying payment check...");
            handleCallback();
          } else {
            setStatus("unknown");
            setMessage("Unable to verify payment status. Please check your purchase history.");

            setTimeout(() => {
              navigate("/dashboard");
            }, 5000);
          }
        }, 2000);

      } catch (error) {
        console.error("Error in PayOP callback:", error);
        setStatus("error");
        setMessage("An error occurred while processing your payment.");

        setTimeout(() => {
          navigate("/dashboard");
        }, 5000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const getStatusContent = () => {
    switch (status) {
      case "checking":
      case "checking_pending":
      case "processing":
        return (
          <div className="text-center">
            <Loader />
            <p className="mt-4 text-lg">{message || "Checking payment status..."}</p>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-lg mb-4">{message}</p>
            <p className="text-gray-600">Redirecting to complete your purchase...</p>
          </div>
        );

      case "failed":
        return (
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">✗</div>
            <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
            <p className="text-lg mb-4">{message}</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </button>
          </div>
        );

      case "error":
        return (
          <div className="text-center">
            <div className="text-yellow-500 text-6xl mb-4">⚠</div>
            <h2 className="text-2xl font-bold mb-2">Error</h2>
            <p className="text-lg mb-4">{message}</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </button>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="text-gray-500 text-6xl mb-4">?</div>
            <h2 className="text-2xl font-bold mb-2">Payment Status Unknown</h2>
            <p className="text-lg mb-4">{message}</p>
            <p className="text-gray-600">Please check your purchase history in the dashboard.</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        {getStatusContent()}
      </div>
    </div>
  );
};

export default PayopCallback;